import { create } from 'zustand';
import { fetchRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from "../api/fields";

const FV = FIELDS.volunteers;
const FVE = FIELDS.volunteerEvents;
const FVA = FIELDS.volunteerAssignments;

// ── Interfaces ─────────────────────────────────────────

export interface Volunteer {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  skills: string;
  certifications: string;
  availability: string;
  emergencyContact: string;
  emergencyPhone: string;
  status: string;
  notes: string;
  [k: string]: unknown;
}

export interface VolEvent {
  id: number;
  incidentId: number | null;
  eventName: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  location: string;
  description: string;
  requiredSkills: string;
  maxVolunteers: number;
  checkInCode: string;
  status: string;
  notes: string;
  [k: string]: unknown;
}

export interface VolAssignment {
  id: number;
  eventId: number;
  volunteerId: number;
  volunteerName: string;
  role: string;
  status: string;
  checkInTime: string;
  checkOutTime: string;
  hoursWorked: string;
  notes: string;
  [k: string]: unknown;
}

// ── Mappers ────────────────────────────────────────────

function mapVol(r: Record<string, unknown>): Volunteer {
  return {
    id: r.record_id as number,
    firstName: (r[FV.firstName] as string) || '',
    lastName: (r[FV.lastName] as string) || '',
    email: (r[FV.email] as string) || '',
    phone: (r[FV.phone] as string) || '',
    address: (r[FV.address] as string) || '',
    city: (r[FV.city] as string) || '',
    state: (r[FV.state] as string) || '',
    zip: (r[FV.zip] as string) || '',
    skills: (r[FV.skills] as string) || '',
    certifications: (r[FV.certifications] as string) || '',
    availability: (r[FV.availability] as string) || '',
    emergencyContact: (r[FV.emergencyContact] as string) || '',
    emergencyPhone: (r[FV.emergencyPhone] as string) || '',
    status: (r[FV.status] as string) || 'active',
    notes: (r[FV.notes] as string) || '',
  };
}

function mapVolEvent(r: Record<string, unknown>): VolEvent {
  return {
    id: r.record_id as number,
    incidentId: (r[FVE.incidentId] as number) || null,
    eventName: (r[FVE.eventName] as string) || '',
    eventDate: (r[FVE.eventDate] as string) || '',
    startTime: (r[FVE.startTime] as string) || '',
    endTime: (r[FVE.endTime] as string) || '',
    location: (r[FVE.location] as string) || '',
    description: (r[FVE.description] as string) || '',
    requiredSkills: (r[FVE.requiredSkills] as string) || '',
    maxVolunteers: Number(r[FVE.maxVolunteers]) || 0,
    checkInCode: (r[FVE.checkInCode] as string) || '',
    status: (r[FVE.status] as string) || 'active',
    notes: (r[FVE.notes] as string) || '',
  };
}

function mapVolAssign(r: Record<string, unknown>): VolAssignment {
  return {
    id: r.record_id as number,
    eventId: Number(r[FVA.eventId]) || 0,
    volunteerId: Number(r[FVA.volunteerId]) || 0,
    volunteerName: (r[FVA.volunteerName] as string) || '',
    role: (r[FVA.role] as string) || '',
    status: (r[FVA.status] as string) || 'scheduled',
    checkInTime: (r[FVA.checkInTime] as string) || '',
    checkOutTime: (r[FVA.checkOutTime] as string) || '',
    hoursWorked: (r[FVA.hoursWorked] as string) || '',
    notes: (r[FVA.notes] as string) || '',
  };
}

// ── Field builder helpers ──────────────────────────────

function buildVolFields(data: Record<string, unknown>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.firstName != null) p[FV.firstName] = data.firstName;
  if (data.lastName != null) p[FV.lastName] = data.lastName;
  if (data.email != null) p[FV.email] = data.email;
  if (data.phone != null) p[FV.phone] = data.phone;
  if (data.address != null) p[FV.address] = data.address;
  if (data.city != null) p[FV.city] = data.city;
  if (data.state != null) p[FV.state] = data.state;
  if (data.zip != null) p[FV.zip] = data.zip;
  if (data.skills != null) p[FV.skills] = data.skills;
  if (data.certifications != null) p[FV.certifications] = data.certifications;
  if (data.availability != null) p[FV.availability] = data.availability;
  if (data.emergencyContact != null) p[FV.emergencyContact] = data.emergencyContact;
  if (data.emergencyPhone != null) p[FV.emergencyPhone] = data.emergencyPhone;
  if (data.status != null) p[FV.status] = data.status;
  if (data.notes != null) p[FV.notes] = data.notes;
  return p;
}

function buildEventFields(data: Record<string, unknown>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.incidentId != null) p[FVE.incidentId] = data.incidentId;
  if (data.eventName != null) p[FVE.eventName] = data.eventName;
  if (data.eventDate != null) p[FVE.eventDate] = data.eventDate;
  if (data.startTime != null) p[FVE.startTime] = data.startTime;
  if (data.endTime != null) p[FVE.endTime] = data.endTime;
  if (data.location != null) p[FVE.location] = data.location;
  if (data.description != null) p[FVE.description] = data.description;
  if (data.requiredSkills != null) p[FVE.requiredSkills] = data.requiredSkills;
  if (data.maxVolunteers != null) p[FVE.maxVolunteers] = data.maxVolunteers;
  if (data.checkInCode != null) p[FVE.checkInCode] = data.checkInCode;
  if (data.status != null) p[FVE.status] = data.status;
  if (data.notes != null) p[FVE.notes] = data.notes;
  return p;
}

function buildAssignFields(data: Record<string, unknown>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.eventId != null) p[FVA.eventId] = data.eventId;
  if (data.volunteerId != null) p[FVA.volunteerId] = data.volunteerId;
  if (data.volunteerName != null) p[FVA.volunteerName] = data.volunteerName;
  if (data.role != null) p[FVA.role] = data.role;
  if (data.status != null) p[FVA.status] = data.status;
  if (data.checkInTime != null) p[FVA.checkInTime] = data.checkInTime;
  if (data.checkOutTime != null) p[FVA.checkOutTime] = data.checkOutTime;
  if (data.hoursWorked != null) p[FVA.hoursWorked] = data.hoursWorked;
  if (data.notes != null) p[FVA.notes] = data.notes;
  return p;
}

// ── Extras helpers (JSON in notes field) ───────────────

export function parseExtras(notes: string): Record<string, unknown> {
  if (!notes) return {};
  try { return JSON.parse(notes); } catch { return {}; }
}

export function mergeExtras(notes: string, patch: Record<string, unknown>): string {
  const existing = parseExtras(notes);
  return JSON.stringify({ ...existing, ...patch });
}

// ── Store ──────────────────────────────────────────────

interface VolunteerState {
  volunteers: Volunteer[];
  events: VolEvent[];
  assignments: VolAssignment[];
  loading: boolean;

  fetchVolunteers: () => Promise<void>;
  fetchEvents: () => Promise<void>;
  fetchAssignments: () => Promise<void>;

  createVolunteer: (data: Record<string, unknown>) => Promise<number | null>;
  updateVolunteer: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteVolunteer: (id: number) => Promise<void>;

  createEvent: (data: Record<string, unknown>) => Promise<number | null>;
  updateEvent: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteEvent: (id: number) => Promise<void>;

  createAssignment: (data: Record<string, unknown>) => Promise<number | null>;
  updateAssignment: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteAssignment: (id: number) => Promise<void>;
}

export const useVolunteerStore = create<VolunteerState>((set, get) => ({
  volunteers: [], events: [], assignments: [], loading: false,

  // ── Fetch ──────────────────────────────────────────
  fetchVolunteers: async () => {
    try {
      set({ loading: true });
      const r = await fetchRecords(TABLES.volunteers, { limit: 500 });
      set({ volunteers: r.records.map(mapVol), loading: false });
    } catch (e) { console.error('[Vol]', e); set({ loading: false }); }
  },
  fetchEvents: async () => {
    try {
      const r = await fetchRecords(TABLES.volunteerEvents, { limit: 200, sort: { field: 'created_at', direction: 'desc' } });
      set({ events: r.records.map(mapVolEvent) });
    } catch (e) { console.error('[Vol]', e); }
  },
  fetchAssignments: async () => {
    try {
      const r = await fetchRecords(TABLES.volunteerAssignments, { limit: 1000 });
      set({ assignments: r.records.map(mapVolAssign) });
    } catch (e) { console.error('[Vol]', e); }
  },

  // ── Volunteers CRUD ────────────────────────────────
  createVolunteer: async (data) => {
    try {
      const r = await createRecord<{id: number}>(TABLES.volunteers, buildVolFields(data));
      await get().fetchVolunteers();
      return r.id;
    } catch (e) { console.error('[Vol]', e); return null; }
  },
  updateVolunteer: async (id, data) => {
    try {
      await updateRecord(TABLES.volunteers, id, buildVolFields(data));
      await get().fetchVolunteers();
    } catch (e) { console.error('[Vol]', e); }
  },
  deleteVolunteer: async (id) => {
    try {
      await deleteRecord(TABLES.volunteers, id);
      set(s => ({ volunteers: s.volunteers.filter(v => v.id !== id) }));
    } catch (e) { console.error('[Vol]', e); }
  },

  // ── Events CRUD ────────────────────────────────────
  createEvent: async (data) => {
    try {
      const r = await createRecord<{id: number}>(TABLES.volunteerEvents, buildEventFields(data));
      await get().fetchEvents();
      return r.id;
    } catch (e) { console.error('[Vol]', e); return null; }
  },
  updateEvent: async (id, data) => {
    try {
      await updateRecord(TABLES.volunteerEvents, id, buildEventFields(data));
      await get().fetchEvents();
    } catch (e) { console.error('[Vol]', e); }
  },
  deleteEvent: async (id) => {
    try {
      await deleteRecord(TABLES.volunteerEvents, id);
      set(s => ({ events: s.events.filter(e => e.id !== id) }));
    } catch (e) { console.error('[Vol]', e); }
  },

  // ── Assignments CRUD ───────────────────────────────
  createAssignment: async (data) => {
    try {
      const r = await createRecord<{id: number}>(TABLES.volunteerAssignments, buildAssignFields(data));
      await get().fetchAssignments();
      return r.id;
    } catch (e) { console.error('[Vol]', e); return null; }
  },
  updateAssignment: async (id, data) => {
    try {
      await updateRecord(TABLES.volunteerAssignments, id, buildAssignFields(data));
      await get().fetchAssignments();
    } catch (e) { console.error('[Vol]', e); }
  },
  deleteAssignment: async (id) => {
    try {
      await deleteRecord(TABLES.volunteerAssignments, id);
      set(s => ({ assignments: s.assignments.filter(a => a.id !== id) }));
    } catch (e) { console.error('[Vol]', e); }
  },

  // V1 compat aliases
  isLoading: false,
  loadVolunteers: async () => { await (get() as any).fetchVolunteers(); },
  loadEvents: async () => { await (get() as any).fetchEvents(); },
  loadAssignments: async () => { await (get() as any).fetchAssignments(); },
}));
